// @ts-nocheck

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Divider,
  VStack,
  HStack,
  Grid,
  GridItem,
  Progress,
  useColorModeValue,
  Link,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useClipboard,
  Textarea,
} from '@chakra-ui/react';
import { FaCreditCard } from 'react-icons/fa';
import axios from 'axios';
import { UpdateCardModal } from './update-card.component';
import { getFirebaseIdToken } from '../../utils/utils';
import {
  useGetCustomerInfoQuery,
  useGetTotalVerificationsQuery,
  useGetSavedCardInfoQuery,
  useGetCustomerPaymentsQuery,
  useCreateSubscriptionCheckoutSessionMutation,
} from '../../redux/payments/payments';
import { useAppSelector } from '../../redux/hooks';
import { getNotifications } from '../../redux/notifications/selectors';
import { SavedCardInfo, Payment, TotalVerificationsData, SavedCard } from './types';
import { PaymentHistory } from './payment-history.component';
import { SuccessDisplay } from './payment-status.component';

const UsageSummary = ({
  isLoading,
  totalVerificationsThisMonthCount,
  zkFetchApiCallsThisMonth,
  handleUpgrade,
}) => {
  const isProofsLimitReached = totalVerificationsThisMonthCount >= 200;
  const isZkFetchLimitReached = zkFetchApiCallsThisMonth >= 1000;
  const isLimitReached = isProofsLimitReached || isZkFetchLimitReached;
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={6}
      bg={useColorModeValue('white', 'gray.700')}
      shadow="md"
    >
      <Heading size="md" mb={2}>
        Free Tier Usage for {currentMonth}
      </Heading>
      <Text fontSize="3xl" fontWeight="bold">
        $0 <span style={{ fontSize: 'large', fontWeight: 'normal' }}>per month</span>
      </Text>
      <Divider my={4} />

      {isLoading ? (
        <Text>Loading usage data...</Text>
      ) : totalVerificationsThisMonthCount !== undefined &&
        zkFetchApiCallsThisMonth !== undefined ? (
        <>
          <UsageBar
            label="Proofs"
            used={totalVerificationsThisMonthCount}
            total={200}
            isLimitReached={isProofsLimitReached}
          />
          <UsageBar
            label="ZK Fetch API Calls"
            used={zkFetchApiCallsThisMonth}
            total={1000}
            isLimitReached={isZkFetchLimitReached}
          />

          <Alert
            status={isLimitReached ? 'error' : 'info'}
            variant="subtle"
            flexDirection="column"
            alignItems="flex-start"
            mt={4}
            borderRadius="md"
          >
            <Flex alignItems="center">
              <AlertIcon />
              <AlertTitle mb={0} ml={0}>
                {isLimitReached ? 'Upgrade Required' : 'Free Plan'}
              </AlertTitle>
            </Flex>
            <AlertDescription>
              {isLimitReached
                ? 'You have reached the limit of your free plan. Please upgrade your plan to continue using the service.'
                : `You are currently on the Free Plan. After reaching the limit for proofs or ZK Fetch usage, you'll need to upgrade to continue using the service.`}
            </AlertDescription>
          </Alert>

          {isLimitReached && (
            <Button colorScheme="teal" mt={6} onClick={handleUpgrade}>
              Upgrade Now
            </Button>
          )}

          <Button colorScheme="blue" mt={4} onClick={handleUpgrade} width="100%">
            Need Higher Limits? Contact Sales
          </Button>
        </>
      ) : (
        <Text>Failed to load usage data. Please try again later.</Text>
      )}
    </Box>
  );
};

const UsageBar = ({ label, used, total, isLimitReached }) => (
  <Box mt={4}>
    <Flex justify="space-between" align="center" mb={2}>
      <Text>{label}</Text>
      <Text fontWeight="bold">
        {total} {label} / month
      </Text>
    </Flex>
    <Progress
      value={(used / total) * 100}
      colorScheme={isLimitReached ? 'red' : 'teal'}
    />
    <Text mt={2} fontSize="sm" color={isLimitReached ? 'red.500' : 'gray.500'}>
      {used} used / {Math.max(0, total - used)} remaining this month
    </Text>
  </Box>
);

const UpcomingBill = () => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={6}
      bg={useColorModeValue('white', 'gray.700')}
      shadow="md"
    >
      <Heading size="md" mb={4}>
        Upcoming Bill
      </Heading>
      <VStack align="stretch" spacing={3} fontSize="sm">
        <HStack justifyContent="space-between">
          <Text>Custom Plan</Text>
          <Text fontWeight="bold">$0</Text>
        </HStack>
        <HStack justifyContent="space-between">
          <Text>Tax:</Text>
          <Text fontWeight="bold">$0.00</Text>
        </HStack>
        <HStack justifyContent="space-between">
          <Text>Total:</Text>
          <Text fontWeight="bold">$0</Text>
        </HStack>
        <Text color="gray.500" fontSize="xs" align="right">
          Will be auto-deducted on the 1st of every month
        </Text>
      </VStack>
    </Box>
  );
};

const BillingInformation = ({ isLoading, savedCardInfo }) => {
  return (
    <Box
      mt={6}
      borderWidth="1px"
      borderRadius="lg"
      p={6}
      bg={useColorModeValue('white', 'gray.700')}
      shadow="md"
    >
      <Heading size="md" mb={4}>
        Billing Information
      </Heading>
      {isLoading ? (
        <Text>Loading saved card information...</Text>
      ) : savedCardInfo?.hasAutoDeductCard ? (
        <VStack align="stretch" spacing={4}>
          <Box>
            <HStack>
              <FaCreditCard size="24px" />
              <Text fontWeight="bold">
                {`${
                  savedCardInfo.autoDeductCard.brand.charAt(0).toUpperCase() +
                  savedCardInfo.autoDeductCard.brand.slice(1)
                } •••• ${savedCardInfo.autoDeductCard.last4}`}
              </Text>
            </HStack>
            <SimpleGrid columns={2} spacing={2} mt={2}>
              <Box>
                <Text fontWeight="semibold">Name on Card</Text>
                <Text>{savedCardInfo.autoDeductCard.nameOnCard}</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold">Expiration</Text>
                <Text>
                  {`${savedCardInfo.autoDeductCard.expMonth.toString().padStart(2, '0')}/${
                    savedCardInfo.autoDeductCard.expYear
                  }`}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      ) : (
        <Text>No auto-deduct card on file.</Text>
      )}
    </Box>
  );
};

const PaymentScreen = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [usage, setUsage] = useState({ used: 0, total: 1000 });
  const toast = useToast();
  const [isUpdateCardModalOpen, setIsUpdateCardModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const { hasCopied, onCopy } = useClipboard(upgradeMessage);

  const {
    data,
    isLoading: isLoadingTotalVerifications,
    refetch: refetchTotalVerifications,
  } = useGetTotalVerificationsQuery();

  const totalVerificationsThisMonthCount = data?.totalVerificationsThisMonthCount;
  const zkFetchApiCallsThisMonth = data?.zkFetchApiCallsThisMonth;

  const {
    data: customerInfo,
    isLoading: isLoadingCustomerInfo,
    refetch: refetchCustomerInfo,
  } = useGetCustomerInfoQuery();

  const customerEmail =
    customerInfo?.customer.billingEmail ?? customerInfo?.customer.email;

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('success')) {
      setSuccess(true);
      setSessionId(query.get('session_id') || '');
    }

    if (query.get('canceled')) {
      toast({
        title: 'Subscription Canceled',
        description: "You can try again when you're ready.",
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const [createCheckoutSession] = useCreateSubscriptionCheckoutSessionMutation();

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const result = await createCheckoutSession().unwrap();
      if (result.sessionUrl) {
        window.location.href = result.sessionUrl;
      } else {
        throw new Error('No session URL returned');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create checkout session. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const { data: savedCardInfo, isLoading: isLoadingSavedCard } =
    useGetSavedCardInfoQuery<SavedCardInfo>(customerInfo?.customer?.customerId);

  const { data: previousPayments, isLoading: isLoadingPayments } =
    useGetCustomerPaymentsQuery(customerInfo?.customer?.customerId as string);

  useEffect(() => {
    refetchTotalVerifications();
    refetchCustomerInfo();
  }, [refetchTotalVerifications, refetchCustomerInfo]);

  const handleUpgrade = () => {
    const message =
      encodeURIComponent(`Hi, I'm interested in custom pricing for more API calls and proofs.

Email: ${customerEmail} 
Customer ID: ${customerInfo?.customer?.customerId}
Current Usage:
- Proofs: ${totalVerificationsThisMonthCount} / 200
- ZK Fetch API Calls: ${zkFetchApiCallsThisMonth} / 1000

I'm looking for a plan with increased limits. Can you help me with pricing options?`);

    const telegramUrl = `https://t.me/sriharshakaramchati?text=${message}`;
    window.open(telegramUrl, '_blank');
  };

  const handleOpenTelegramChat = () => {
    window.open('https://t.me/sriharshakaramchati', '_blank');
  };

  return (
    <Box p={8} maxW="1200px" mx="auto">
      <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={8}>
        <GridItem>
          <VStack spacing={6} align="stretch">
            <UsageSummary
              isLoading={isLoadingTotalVerifications}
              totalVerificationsThisMonthCount={totalVerificationsThisMonthCount}
              zkFetchApiCallsThisMonth={zkFetchApiCallsThisMonth}
              handleUpgrade={handleUpgrade}
            />
            <Box
              borderWidth="1px"
              borderRadius="lg"
              p={6}
              bg={useColorModeValue('white', 'gray.700')}
              shadow="md"
            >
              <Heading size="md" mb={4}>
                Past Payment History
              </Heading>
              {isLoadingPayments ? (
                <Text>Loading payment history...</Text>
              ) : (
                <PaymentHistory payments={previousPayments?.payments ?? []} />
              )}
            </Box>
          </VStack>
        </GridItem>

        <GridItem>
          <UpcomingBill />
          <BillingInformation
            isLoading={isLoadingSavedCard}
            savedCardInfo={savedCardInfo}
          />
        </GridItem>
      </Grid>

      {success && <SuccessDisplay sessionId={sessionId} />}
    </Box>
  );
};

export { PaymentScreen };
